import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { API_BASE_URL, getPlayModeLabel } from '../App';
import LoginModal from './LoginModal';
import { filterAndRankTnPlates, TN_COLOR_PRESETS } from '../lib/tnPlateSearch';
import './TennesseePlatesPage.css';

const VOICE_MAX_MS = 8000;

function plateImageSrc(imageFile) {
	if (!imageFile) return null;
	return `${process.env.PUBLIC_URL || ''}/assets/tn-plates/${imageFile}`;
}

function plateDisplayName(plate) {
	return plate?.commonName || plate?.officialName || plate?.id || '';
}

function seenSetForMode(game, tnPlayMode) {
	if (!game) return new Set();
	if (tnPlayMode === 'monthly') return new Set(game.visitedPlates || []);
	const unions = game.windowPlateUnions || {};
	if (tnPlayMode === 'annual') return new Set(unions.annual || game.visitedPlates || []);
	if (tnPlayMode === 'decennial') return new Set(unions.decennial || game.visitedPlates || []);
	return new Set(game.visitedPlates || []);
}

function tierForPlate(plateId, game, tnPlayMode) {
	if (tnPlayMode !== 'monthly' && game?.plateTiers?.[plateId]) {
		return game.plateTiers[plateId];
	}
	if ((game?.visitedPlates || []).includes(plateId)) return 'current_month';
	return 'none';
}

function getSpeechRecognitionCtor() {
	if (typeof window === 'undefined') return null;
	return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function transcriptFromSpeechResults(results) {
	if (!results?.length) return '';
	let finalText = '';
	let interim = '';
	for (let i = 0; i < results.length; i += 1) {
		const piece = (results[i][0]?.transcript || '').trim();
		if (!piece) continue;
		if (results[i].isFinal) finalText = finalText ? `${finalText} ${piece}` : piece;
		else interim = interim ? `${interim} ${piece}` : piece;
	}
	return [finalText, interim].filter(Boolean).join(' ');
}

function voiceErrorMessage(code) {
	switch (code) {
		case 'not-allowed':
		case 'service-not-allowed':
			return 'Microphone is blocked. Allow it for this site, or type instead.';
		case 'audio-capture':
			return 'No microphone found.';
		case 'network':
			return 'Voice search needs a network connection.';
		case 'no-speech':
			return 'Didn’t catch that. Tap Mic and try again.';
		case 'aborted':
			return null;
		default:
			return 'Could not hear that. Try again or type it.';
	}
}

function ReverseSortIcon({ reversed }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width="20"
			height="20"
			aria-hidden="true"
			focusable="false"
			style={{ transform: reversed ? 'scaleY(-1)' : undefined }}
		>
			<path
				fill="currentColor"
				d="M11 5h2v10.2l3.3-3.3 1.4 1.4L12 19l-5.7-5.7 1.4-1.4L11 15.2V5z"
			/>
		</svg>
	);
}

function FilterIcon() {
	return (
		<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
			<path fill="currentColor" d="M3 5h18v2.2l-7 7.8V21h-4v-6L3 7.2V5z" />
		</svg>
	);
}

function FilterControls({
	seenFilter,
	setSeenFilter,
	colorPreset,
	setColorPreset,
	category,
	setCategory,
	categories,
}) {
	return (
		<>
			<div className="tn-plates__chips" role="tablist" aria-label="Seen filter">
				{[
					{ id: 'all', label: 'All' },
					{ id: 'unseen', label: 'Unseen' },
					{ id: 'seen', label: 'Seen' },
				].map((opt) => (
					<button
						key={opt.id}
						type="button"
						className={`tn-plates__chip ${seenFilter === opt.id ? 'is-active' : ''}`}
						onClick={() => setSeenFilter(opt.id)}
					>
						{opt.label}
					</button>
				))}
			</div>
			<div className="tn-plates__chips" role="tablist" aria-label="Color">
				{TN_COLOR_PRESETS.map((preset) => (
					<button
						key={preset.id}
						type="button"
						className={`tn-plates__chip tn-plates__chip--color ${
							colorPreset === preset.id ? 'is-active' : ''
						}`}
						onClick={() => setColorPreset(preset.id)}
					>
						{preset.swatch && (
							<span
								className="tn-plates__chip-swatch"
								style={{ background: preset.swatch }}
								aria-hidden="true"
							/>
						)}
						{preset.label}
					</button>
				))}
			</div>
			<div className="tn-plates__chips" role="tablist" aria-label="Category">
				<button
					type="button"
					className={`tn-plates__chip ${category === '' ? 'is-active' : ''}`}
					onClick={() => setCategory('')}
				>
					Every category
				</button>
				{categories.map((cat) => (
					<button
						key={cat}
						type="button"
						className={`tn-plates__chip ${category === cat ? 'is-active' : ''}`}
						onClick={() => setCategory(cat)}
					>
						{cat}
					</button>
				))}
			</div>
		</>
	);
}

const TennesseePlatesPage = ({ user }) => {
	const plateMatch = useMatch('/tennessee/plates/:plateId');
	const navigate = useNavigate();
	const sessionId = Cookies.get('session');
	const [catalog, setCatalog] = useState([]);
	const [categories, setCategories] = useState([]);
	const [catalogError, setCatalogError] = useState(null);
	const [loadingCatalog, setLoadingCatalog] = useState(true);
	const [query, setQuery] = useState('');
	const [category, setCategory] = useState('');
	const [seenFilter, setSeenFilter] = useState('all');
	const [colorPreset, setColorPreset] = useState('all');
	const [colorSortDesc, setColorSortDesc] = useState(false);
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [game, setGame] = useState(null);
	const [savingId, setSavingId] = useState(null);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [confirmRemove, setConfirmRemove] = useState(false);
	const [actionError, setActionError] = useState(null);
	const [voiceSupported, setVoiceSupported] = useState(false);
	const [listening, setListening] = useState(false);
	const recognitionRef = useRef(null);
	const voiceTimerRef = useRef(null);

	const tnPlayMode = game?.tnPlayMode || user?.tnPlayMode || 'monthly';
	const tnUnlocked = Boolean(user?.tnPlatesEnabled);
	const plateId = plateMatch?.params?.plateId ? String(plateMatch.params.plateId) : '';

	const loadCatalog = useCallback(() => {
		setLoadingCatalog(true);
		return fetch(`${API_BASE_URL}/tn-plates`)
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json();
			})
			.then((data) => {
				setCatalog(data.plates || []);
				setCategories(data.categories || []);
				setCatalogError(null);
			})
			.catch((err) => {
				console.error('TN catalog failed', err);
				setCatalogError('Could not load Tennessee plates. Try again in a moment.');
			})
			.finally(() => setLoadingCatalog(false));
	}, []);

	const loadGame = useCallback(() => {
		if (!sessionId) {
			setGame(null);
			return Promise.resolve();
		}
		return fetch(`${API_BASE_URL}/tn-game`, {
			headers: { Authorization: sessionId },
		})
			.then((res) => {
				if (res.status === 401) {
					setGame(null);
					return null;
				}
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json();
			})
			.then((data) => {
				if (data) setGame(data);
			})
			.catch((err) => console.error('TN game failed', err));
	}, [sessionId]);

	useEffect(() => {
		loadCatalog();
	}, [loadCatalog]);

	useEffect(() => {
		loadGame();
	}, [loadGame]);

	useEffect(() => {
		setVoiceSupported(Boolean(getSpeechRecognitionCtor()));
		return () => {
			if (voiceTimerRef.current) clearTimeout(voiceTimerRef.current);
			try {
				recognitionRef.current?.stop();
			} catch {
				/* ignore */
			}
		};
	}, []);

	const closePlate = useCallback(() => {
		setConfirmRemove(false);
		navigate('/tennessee');
	}, [navigate]);

	useEffect(() => {
		if (!plateId && !filtersOpen) return undefined;
		const onKey = (event) => {
			if (event.key !== 'Escape') return;
			if (filtersOpen) {
				setFiltersOpen(false);
				return;
			}
			closePlate();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [plateId, filtersOpen, closePlate]);

	useEffect(() => {
		const lock = Boolean(plateId) || filtersOpen;
		if (!lock) return undefined;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [plateId, filtersOpen]);

	useEffect(() => {
		setConfirmRemove(false);
		if (plateId) setFiltersOpen(false);
	}, [plateId]);

	const seenIds = useMemo(() => seenSetForMode(game, tnPlayMode), [game, tnPlayMode]);
	const currentMonthIds = useMemo(() => new Set(game?.visitedPlates || []), [game]);

	const colorFilter = useMemo(() => {
		const preset = TN_COLOR_PRESETS.find((p) => p.id === colorPreset) || TN_COLOR_PRESETS[0];
		if (preset.mode === 'all') return { mode: 'all' };
		if (preset.mode === 'neutral') return { mode: 'neutral' };
		return {
			mode: 'hue',
			hueMin: preset.hueMin,
			hueMax: preset.hueMax,
			includeNeutrals: false,
		};
	}, [colorPreset]);

	const visiblePlates = useMemo(() => {
		let list = filterAndRankTnPlates(catalog, query, category, colorFilter);
		if (seenFilter === 'seen') list = list.filter((p) => seenIds.has(p.id));
		if (seenFilter === 'unseen') list = list.filter((p) => !seenIds.has(p.id));
		if (colorSortDesc) list = [...list].reverse();
		return list;
	}, [catalog, query, category, colorFilter, seenFilter, seenIds, colorSortDesc]);

	const openPlate = useMemo(
		() => (plateId ? catalog.find((p) => p.id === plateId) || null : null),
		[catalog, plateId],
	);

	const progressCount = game?.progress?.count ?? seenIds.size;
	const denominator = game?.progress?.denominator ?? catalog.filter((p) => p.active !== false).length;
	const activeFilterCount =
		(seenFilter !== 'all' ? 1 : 0) + (colorPreset !== 'all' ? 1 : 0) + (category ? 1 : 0);

	const applyMutation = async (nextVisited) => {
		if (!sessionId) return;
		const res = await fetch(`${API_BASE_URL}/tn-game`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				Authorization: sessionId,
			},
			body: JSON.stringify({ visitedPlates: nextVisited }),
		});
		const data = await res.json().catch(() => ({}));
		if (!res.ok) {
			throw new Error(data.message || data.error || `HTTP ${res.status}`);
		}
		setGame(data);
	};

	const markSeen = async (plate) => {
		setActionError(null);
		if (!user) {
			setShowLoginModal(true);
			return;
		}
		if (!tnUnlocked) {
			navigate('/account');
			return;
		}
		if (currentMonthIds.has(plate.id)) return;
		setSavingId(plate.id);
		try {
			await applyMutation([...(game?.visitedPlates || []), plate.id]);
		} catch (err) {
			setActionError(err.message || 'Could not save that sighting.');
		} finally {
			setSavingId(null);
		}
	};

	const removeSighting = async (plate) => {
		setActionError(null);
		setSavingId(plate.id);
		try {
			await applyMutation((game?.visitedPlates || []).filter((id) => id !== plate.id));
			setConfirmRemove(false);
		} catch (err) {
			setActionError(err.message || 'Could not remove that sighting.');
		} finally {
			setSavingId(null);
		}
	};

	const toggleVoiceSearch = () => {
		const Ctor = getSpeechRecognitionCtor();
		if (!Ctor) {
			setActionError('Voice search needs Chrome, Edge, or Safari — it uses the browser’s speech-to-text, not an AI model.');
			return;
		}
		if (listening && recognitionRef.current) {
			try {
				recognitionRef.current.stop();
			} catch {
				/* ignore */
			}
			return;
		}
		setActionError(null);
		const recognition = new Ctor();
		recognitionRef.current = recognition;
		recognition.lang = 'en-US';
		recognition.interimResults = true;
		recognition.continuous = false;
		recognition.maxAlternatives = 1;
		let settled = false;
		const finish = () => {
			if (settled) return;
			settled = true;
			if (voiceTimerRef.current) {
				clearTimeout(voiceTimerRef.current);
				voiceTimerRef.current = null;
			}
			setListening(false);
			if (recognitionRef.current === recognition) recognitionRef.current = null;
		};
		recognition.onstart = () => setListening(true);
		recognition.onend = finish;
		recognition.onerror = (event) => {
			finish();
			const message = voiceErrorMessage(event.error);
			if (message) setActionError(message);
		};
		recognition.onresult = (event) => {
			const next = transcriptFromSpeechResults(event.results);
			if (next) setQuery(next);
		};
		voiceTimerRef.current = setTimeout(() => {
			try {
				recognition.stop();
			} catch {
				/* ignore */
			}
		}, VOICE_MAX_MS);
		try {
			recognition.start();
		} catch {
			finish();
			setActionError('Microphone is already in use.');
		}
	};

	const showGallery = tnUnlocked;
	const openTier = openPlate ? tierForPlate(openPlate.id, game, tnPlayMode) : 'none';
	const openSeen = openPlate ? seenIds.has(openPlate.id) : false;
	const openSrc = openPlate ? plateImageSrc(openPlate.imageFile) : null;

	return (
		<div className="tn-plates">
			{!showGallery && !plateId ? (
				<header className="tn-plates__hero">
					<p className="tn-plates__kicker">Optional mode</p>
					<h1 className="tn-plates__title">Tennessee</h1>
					<p className="tn-plates__lede">
						This mini-game is off until you turn it on. It is separate from the North America map.
					</p>
					<p className="tn-plates__lede">
						{user ? (
							<Link to="/account">Enable Tennessee specialty plates on Account</Link>
						) : (
							<Link to="/login">Sign in</Link>
						)}
						{user ? '' : ', then enable it on Account.'}
					</p>
				</header>
			) : (
				<>
			{showGallery && (
			<>
			<header className="tn-plates__hero">
				<p className="tn-plates__kicker">Game within a game</p>
				<h1 className="tn-plates__title">Tennessee</h1>
				<p className="tn-plates__lede">
					Spot specialty designs on Tennessee roads. Search by what you remember — “yellow plate with a
					snake” finds the Gadsden / Don&apos;t Tread on Me tag, even though the state lists it as Friends of
					Sycamore Shoals.
				</p>
				<div className="tn-plates__progress">
					<strong>
						{progressCount} / {denominator}
					</strong>
					<span>
						{getPlayModeLabel(tnPlayMode)} · Tennessee designs
						{!user ? ' · sign in to track' : ''}
					</span>
				</div>
			</header>

			<div className="tn-plates__toolbar">
				<div className="tn-plates__search">
					<label className="tn-plates__search-field">
						<span className="tn-plates__sr">Search plates</span>
						<input
							type="search"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={listening ? 'Listening…' : 'Search plates'}
							autoComplete="off"
							enterKeyHint="search"
						/>
					</label>
					{voiceSupported && (
						<button
							type="button"
							className={`tn-plates__icon-btn ${listening ? 'is-listening' : ''}`}
							onClick={toggleVoiceSearch}
							aria-pressed={listening}
							aria-label={listening ? 'Stop voice search' : 'Search by voice'}
							title={listening ? 'Stop' : 'Voice search'}
						>
							{listening ? 'Stop' : 'Mic'}
						</button>
					)}
					<button
						type="button"
						className={`tn-plates__icon-btn ${colorSortDesc ? 'is-active' : ''}`}
						onClick={() => setColorSortDesc((v) => !v)}
						aria-pressed={colorSortDesc}
						aria-label={colorSortDesc ? 'Color sort reversed. Restore default color order' : 'Reverse color sort'}
						title={colorSortDesc ? 'Default color order' : 'Reverse color sort'}
					>
						<ReverseSortIcon reversed={colorSortDesc} />
					</button>
					<button
						type="button"
						className={`tn-plates__icon-btn tn-plates__filters-btn ${filtersOpen ? 'is-active' : ''}`}
						onClick={() => setFiltersOpen((v) => !v)}
						aria-expanded={filtersOpen}
						aria-controls="tn-plates-filters"
						aria-label="Filters"
						title="Filters"
					>
						<FilterIcon />
						<span>Filters</span>
						{activeFilterCount > 0 && (
							<span className="tn-plates__filter-badge">{activeFilterCount}</span>
						)}
					</button>
				</div>
				{listening && (
					<p className="tn-plates__listening" aria-live="polite">
						Listening — speak now. Tap Stop when you’re done.
					</p>
				)}
			</div>

			{catalogError && <p className="tn-plates__error">{catalogError}</p>}
			{actionError && !plateId && <p className="tn-plates__error">{actionError}</p>}
			{loadingCatalog && <p className="tn-plates__status">Loading plates…</p>}

			{!loadingCatalog && (
				<p className="tn-plates__count">
					{visiblePlates.length} {visiblePlates.length === 1 ? 'plate' : 'plates'}
					{query ? ` matching “${query}”` : colorSortDesc ? ' · reverse color' : ' · sorted by color'}
					{colorPreset !== 'all' ? ` · ${TN_COLOR_PRESETS.find((p) => p.id === colorPreset)?.label || ''}` : ''}
				</p>
			)}
			{!loadingCatalog && visiblePlates.length === 0 && (
				<p className="tn-plates__empty">
					No designs matched. Try another color, or search “yellow snake”, “black plate”, “vols”.
				</p>
			)}

			<ul className="tn-plates__grid">
				{visiblePlates.map((plate) => {
					const tier = tierForPlate(plate.id, game, tnPlayMode);
					const seen = seenIds.has(plate.id);
					const src = plateImageSrc(plate.imageFile);
					return (
						<li key={plate.id}>
							<Link
								to={`/tennessee/plates/${plate.id}`}
								className={`tn-plates__card ${seen ? 'is-seen' : ''} ${
									tier === 'window_only' ? 'is-window' : ''
								}`}
							>
								<div className="tn-plates__art">
									{src ? (
										<img
											src={src}
											alt=""
											width="315"
											height="360"
											loading="lazy"
										/>
									) : (
										<div className="tn-plates__art-fallback" aria-hidden="true">
											TN
										</div>
									)}
									{seen && (
										<span className="tn-plates__badge">
											{tier === 'window_only' ? 'Earlier' : 'Seen'}
										</span>
									)}
								</div>
								<div className="tn-plates__meta">
									<h2 className="tn-plates__name">{plateDisplayName(plate)}</h2>
								</div>
							</Link>
						</li>
					);
				})}
			</ul>

			<p className="tn-plates__legal">
				Plate images from the Tennessee Department of Revenue specialty catalog, hosted here for
				identification (not hotlinked). College, sports, and organizational marks belong to their owners.
				Progress on this page is separate from the North America map game.
			</p>
			</>
			)}

			{!showGallery && plateId && (
				<header className="tn-plates__hero">
					<p className="tn-plates__kicker">Optional mode</p>
					<h1 className="tn-plates__title">Tennessee</h1>
					<p className="tn-plates__lede">
						{user ? (
							<Link to="/account">Enable Tennessee specialty plates on Account</Link>
						) : (
							<Link to="/login">Sign in</Link>
						)}
						{user ? ' to log sightings.' : ', then enable it on Account to log sightings.'}
					</p>
				</header>
			)}

			{filtersOpen && (
				<div className="tn-plates__filters-overlay" onClick={() => setFiltersOpen(false)}>
					<div
						id="tn-plates-filters"
						className="tn-plates__filters-panel"
						role="dialog"
						aria-label="Plate filters"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="tn-plates__filters-head">
							<h2>Filters</h2>
							<button type="button" className="tn-plates__icon-btn" onClick={() => setFiltersOpen(false)}>
								Done
							</button>
						</div>
						<FilterControls
							seenFilter={seenFilter}
							setSeenFilter={setSeenFilter}
							colorPreset={colorPreset}
							setColorPreset={setColorPreset}
							category={category}
							setCategory={setCategory}
							categories={categories}
						/>
					</div>
				</div>
			)}

			{plateId && (
				<div className="tn-plates__detail-overlay" onClick={closePlate}>
					<div
						className="tn-plates__detail"
						role="dialog"
						aria-modal="true"
						aria-labelledby="tn-plate-detail-title"
						onClick={(e) => e.stopPropagation()}
					>
						<button type="button" className="tn-plates__detail-close" onClick={closePlate} aria-label="Close">
							Close
						</button>
						{loadingCatalog && <p className="tn-plates__status">Loading plate…</p>}
						{!loadingCatalog && !openPlate && (
							<>
								<h2 id="tn-plate-detail-title">Plate not found</h2>
								<p>That Tennessee design is not in the catalog.</p>
								<button type="button" className="tn-plates__primary" onClick={closePlate}>
									Back to Tennessee
								</button>
							</>
						)}
						{openPlate && (
							<>
								<div className="tn-plates__detail-art">
									{openSrc ? (
										<img src={openSrc} alt={plateDisplayName(openPlate)} width="315" height="360" />
									) : (
										<div className="tn-plates__art-fallback">TN</div>
									)}
								</div>
								<h2 id="tn-plate-detail-title" className="tn-plates__detail-title">
									{plateDisplayName(openPlate)}
								</h2>
								{actionError && <p className="tn-plates__error">{actionError}</p>}
								<div className="tn-plates__detail-actions">
									{showGallery && user && openTier === 'current_month' && !confirmRemove && (
										<button
											type="button"
											className="tn-plates__danger"
											onClick={() => setConfirmRemove(true)}
											disabled={savingId === openPlate.id}
										>
											Remove this month&apos;s sighting
										</button>
									)}
									{showGallery && user && openTier === 'current_month' && confirmRemove && (
										<div className="tn-plates__confirm">
											<p>Remove this month&apos;s sighting?</p>
											<div className="tn-plates__modal-actions">
												<button type="button" onClick={() => setConfirmRemove(false)}>
													Keep it
												</button>
												<button
													type="button"
													className="tn-plates__danger"
													onClick={() => removeSighting(openPlate)}
													disabled={savingId === openPlate.id}
												>
													Remove
												</button>
											</div>
										</div>
									)}
									{showGallery && user && openTier !== 'current_month' && (
										<button
											type="button"
											className="tn-plates__primary"
											onClick={() => markSeen(openPlate)}
											disabled={savingId === openPlate.id}
										>
											{openSeen ? 'I’ve seen this this month' : 'I’ve seen this plate'}
										</button>
									)}
									{(!user || !showGallery) && (
										<button
											type="button"
											className="tn-plates__primary"
											onClick={() => (user ? navigate('/account') : setShowLoginModal(true))}
										>
											{user ? 'Enable Tennessee to log this' : 'Sign in to log this plate'}
										</button>
									)}
									{openPlate.sourceUrl && (
										<a className="tn-plates__dor-link" href={openPlate.sourceUrl}>
											Learn more on Tennessee’s site
										</a>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			)}

			<LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
				</>
			)}
		</div>
	);
};

export default TennesseePlatesPage;
